import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { map, Observable, catchError, of } from 'rxjs';
import { UtilityService } from '../shared/utility.service';
import { authBackendEndPointToken, backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { UserApproval } from '../patient-registration-center/patient-registration-center.resolver';
import { Appointment, Bundle, Patient } from 'fhir/r4';
import { buildPharmacyWalkInTagQuery } from '../medication-dispense/new-medication-dispense/new-medication-dispense.service';
import { DisableCountParam, LoadingUIEnabled } from '../loading.interceptor';

export interface ResourceWrapper<T> {
    referenceId: string | null;
    savedStatus: 'saved' | 'unsaved';
    actualResource: T;
}

@Injectable({
    providedIn: 'root'
})
export class ReceptionistDashboardService {
    private http = inject(HttpClient);
    private utilityService = inject(UtilityService);
    private backendApi = inject(authBackendEndPointToken);
    private backendUrl = inject(backendEndPointToken);
    private terminologyBase = inject(terminologyCanonicalUrlBaseToken);
    private readonly silentCountContext = new HttpContext()
        .set(LoadingUIEnabled, false)
        .set(DisableCountParam, true);

    /**
     * Pending Registrations: From auth backend
     */
    getPendingRegistrationsCount$(): Observable<number> {
        return this.http.get<UserApproval[]>(
            `${this.backendApi}/api/admin/approvals/pending?accountType=Patient`
        ).pipe(
            map((approvals: UserApproval[]) => approvals.length),
            catchError(() => of(0))
        );
    }

    /**
     * Active Patients: approved active patients, regardless of deceased status.
     */
    getActivePatientsCount$(): Observable<number> {
        const params = new URLSearchParams();
        params.set('active', 'true');
        params.set('_summary', 'count');

        return this.http.get<Bundle<Patient>>(`${this.backendUrl}/Patient?${params.toString()}`, {
            context: this.silentCountContext
        }).pipe(
            map((bundle) => bundle.total ?? 0),
            catchError(() => of(0))
        );
    }

    getPharmacyWalkInPatientsCount$(): Observable<number> {
        const params = new URLSearchParams();
        params.set('active', 'true');
        params.set('_tag', buildPharmacyWalkInTagQuery(this.terminologyBase));
        params.set('_summary', 'count');

        return this.http.get<Bundle<Patient>>(`${this.backendUrl}/Patient?${params.toString()}`, {
            context: this.silentCountContext
        }).pipe(
            map((bundle) => bundle.total ?? 0),
            catchError(() => of(0))
        );
    }

    /**
     * Waitlist Size: Appointments with status 'waitlist'
     */
    getWaitlistSize$(): Observable<number> {
        return this.utilityService.threeTierQueryAndSyncToState$<Appointment>({
            resourceType: 'Appointment',
            scope: 'orgWide',
            tier1FollowNextLinks: true
        }).pipe(
            map((appointments: Appointment[]) => {
                return (appointments || []).filter(a => a.status === 'waitlist').length;
            }),
            catchError(() => of(0))
        );
    }

    /**
     * Today's Appointments: Appointments starting today
     */
    getTodaysAppointmentsCount$(): Observable<number> {
        return this.utilityService.threeTierQueryAndSyncToState$<Appointment>({
            resourceType: 'Appointment',
            scope: 'orgWide',
            tier1FollowNextLinks: true
        }).pipe(
            map((appointments: Appointment[]) => {
                const today = new Date().toDateString();
                return (appointments || []).filter(a => a.start && new Date(a.start).toDateString() === today).length;
            }),
            catchError(() => of(0))
        );
    }
}
